import React, { useState, useMemo, useEffect } from "react";
import { useTable, useSortBy, useGlobalFilter } from "react-table";
import { FaEye, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import GlobalFilter from "../components/common/filter";
import { url } from "../constants/url";
import ReactPaginate from "react-paginate";

const PrivateSuite = () => {
  // State for API data and pagination
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");

  const navigate = useNavigate();

  // Function to fetch data from the API
  // Function to fetch data from the API
  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${url}/api/admin/private-suites?page=${page}&limit=${limit}&search=${search}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          }
        }
      );

      // Unauthorized → clear auth and redirect
      if (response.status === 401) {
        localStorage.clear();
        navigate("/login");
        return;
      }

      const data = await response.json();
      if (response.ok) {
        // Map the API data to match your table's expected fields.
        const mappedData = data.data.map((item) => ({
          saloonName:
            item.UserInformation && item.UserInformation.User
              ? item.UserInformation.User.fullName
              : "N/A",
          location: item.UserInformation ? item.UserInformation.city : "N/A",
          joiningDate: item.UserInformation
            ? new Date(item.UserInformation.createdAt).toLocaleDateString()
            : "N/A",
          status: item.status || "free",
          reports: item.reports || 0,
          id: item.id
        }));
        setTableData(mappedData);
        setTotalPages(data.totalPages);
      } else {
        console.error("Error fetching data:", data.message);
        setTableData([]);
      }
    } catch (error) {
      console.error("Error fetching private suites:", error);
      setTableData([]);
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch when page, limit, or search changes.
  useEffect(() => {
    fetchData();
  }, [page, limit, search]);

  // Define table columns
  const columns = useMemo(
    () => [
      { Header: "Saloon Name", accessor: "saloonName" },
      { Header: "Location", accessor: "location" },
      { Header: "Joining Date", accessor: "joiningDate" },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => (
          <span
            className={`px-3 py-1 rounded-3xl text-white ${
              value === "subscribed" ? "bg-green-500" : "bg-yellow-500"
            }`}
          >
            {value.charAt(0).toUpperCase() + value.slice(1)}
          </span>
        )
      },
      { Header: "Reports", accessor: "reports" },
      {
        Header: "View",
        Cell: ({ row }) => (
          <Link to={`/private-suite/${row.original.id}`}>
            <FaEye className="text-blue-500 cursor-pointer" />
          </Link>
        )
      }
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    setGlobalFilter,
    preGlobalFilteredRows
  } = useTable(
    {
      columns,
      data: tableData
    },
    useGlobalFilter,
    useSortBy
  );

  const handleGlobalFilterChange = (value) => {
    setGlobalFilter(value || undefined);
    setSearch(value);
    setPage(1);
  };

  const handlePageClick = ({ selected }) => {
    setPage(selected + 1);
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="p-6 bg-white rounded-3xl shadow-md">
      <h1 className="text-3xl font-semibold mb-4">Private Suite</h1>
      <GlobalFilter
        preGlobalFilteredRows={preGlobalFilteredRows}
        globalFilter={state.globalFilter}
        setGlobalFilter={handleGlobalFilterChange}
      />
      <div className="overflow-x-auto">
        <table {...getTableProps()} className="min-w-full bg-white mt-4">
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className="py-3 px-4 border-b text-left"
                  >
                    <div className="flex items-center">
                      {column.render("Header")}
                      <span className="ml-2">
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <FaSortDown />
                          ) : (
                            <FaSortUp />
                          )
                        ) : (
                          <FaSort />
                        )}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()} className="hover:bg-gray-100">
                  {row.cells.map((cell) => (
                    <td {...cell.getCellProps()} className="py-2 px-4 border-b">
                      {cell.render("Cell")}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Pagination Controls using ReactPaginate */}
      <div className="mt-4 flex justify-center">
        <ReactPaginate
          previousLabel="Previous"
          nextLabel="Next"
          breakLabel="..."
          pageCount={totalPages}
          marginPagesDisplayed={2}
          pageRangeDisplayed={5}
          onPageChange={handlePageClick}
          containerClassName="flex items-center space-x-2"
          pageClassName="px-4 py-2 bg-gray-300 rounded cursor-pointer"
          activeClassName="bg-blue-500 text-white"
          previousClassName="px-4 py-2 bg-gray-300 rounded cursor-pointer"
          nextClassName="px-4 py-2 bg-gray-300 rounded cursor-pointer"
          disabledClassName="opacity-50 cursor-not-allowed"
        />
      </div>
    </div>
  );
};

export default PrivateSuite;
